import { Link } from 'react-router-dom';
import { PosterImage } from '@/components/ui/PosterImage';
import { formatScore } from '@/lib/format';
import { useLibrary } from '@/hooks/useLibrary';
import type { AnimeSummary } from '@/types/anime';

interface AnimeCardProps {
	anime: AnimeSummary;
	/** Nút phụ ở góc trên-phải: tim (mặc định) hoặc xoá khỏi ngăn thư viện. */
	onRemove?: () => void;
	eager?: boolean;
}

export function AnimeCard({ anime, onRemove, eager }: AnimeCardProps) {
	const { isSaved, toggle } = useLibrary();
	const favorited = isSaved('favorites', anime.slug);

	const subtitle =
		[anime.episodeCurrent, anime.year ? String(anime.year) : '']
			.filter(Boolean)
			.join(' • ') || 'Đang cập nhật';

	return (
		<article className="group relative min-w-0">
			{/* Viền gradient chỉ hiện khi hover, giống .card::before của bản gốc */}
			<span
				className="pointer-events-none absolute -inset-px -z-10 rounded-[14px] bg-linear-to-br from-accent/35 via-transparent to-iris/35 opacity-0 transition duration-250 group-hover:opacity-100"
				aria-hidden="true"
			/>

			<Link
				to={`/phim/${anime.slug}`}
				className="block transition-transform duration-200 group-hover:-translate-y-[5px]"
			>
				<div className="relative aspect-[2/2.8] overflow-hidden rounded-card border border-white/[0.06] bg-surface-2">
					<PosterImage
						src={anime.posterUrl}
						alt={`Poster ${anime.title}`}
						eager={eager}
						className="size-full object-cover transition duration-300 group-hover:scale-[1.06]"
					/>

					<span
						className="pointer-events-none absolute inset-x-0 top-[45%] bottom-0 bg-linear-to-b from-transparent to-bg/60"
						aria-hidden="true"
					/>

					<span className="absolute top-2.5 left-2.5 z-10 rounded-md bg-[#191522dd] px-1.5 py-0.5 text-[11px] font-bold text-gold">
						★ {formatScore(anime.score)}
					</span>

					{anime.quality && (
						<span className="absolute bottom-2.5 left-2.5 z-10 rounded-md bg-bg/75 px-1.5 py-0.5 text-[10px] font-bold tracking-wide text-aqua">
							{anime.quality}
						</span>
					)}

					<span
						className="absolute top-1/2 left-1/2 z-20 grid size-[42px] -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-accent text-white opacity-0 shadow-play transition duration-200 group-hover:opacity-100"
						aria-hidden="true"
					>
						▶
					</span>
				</div>

				<h3 className="mt-2.5 truncate text-sm font-semibold">{anime.title}</h3>
				<small className="block truncate text-xs text-muted">{subtitle}</small>
			</Link>

			<button
				type="button"
				onClick={() => (onRemove ? onRemove() : toggle('favorites', anime))}
				aria-label={
					onRemove
						? `Xoá ${anime.title} khỏi danh sách`
						: favorited
							? `Bỏ ${anime.title} khỏi yêu thích`
							: `Thêm ${anime.title} vào yêu thích`
				}
				className={`absolute top-2.5 right-2.5 z-30 grid size-[30px] place-items-center rounded-lg border bg-bg/75 transition duration-200 focus-visible:opacity-100 ${
					onRemove || favorited
						? 'border-accent/40 text-accent-soft opacity-100'
						: 'border-white/10 text-white opacity-0 group-hover:opacity-100'
				}`}
			>
				{onRemove ? '×' : favorited ? '♥' : '♡'}
			</button>
		</article>
	);
}
